// Camera modes: WalkMode (first person with collision), OverviewMode (orbit from above, ceiling cut
// away) and DroneTour (automatic flight through all rooms). All read the shared Input and drive one
// PlayCanvas camera entity. Rotation and tilt go through critically damped springs: turns ease in and
// out instead of starting abruptly, and a resting camera really rests.
import { Vec3 } from 'playcanvas';

const DEG = Math.PI / 180;
const tmpA = new Vec3(), tmpB = new Vec3();
const wrap = d => ((d % 360) + 540) % 360 - 180;
const smooth = t => t * t * (3 - 2 * t);
const damp = (rate, dt) => 1 - Math.exp(-rate * dt);
const approach = (v, target, k, eps) => (Math.abs(target - v) < eps ? target : v + (target - v) * k);
const clampPitch = p => Math.max(-80, Math.min(80, p));

/** World ray from a screen point: {origin:[x,y,z], dir:[x,y,z]}. */
export function screenRay(cameraEntity, x, y) {
    const cam = cameraEntity.camera;
    // screenToWorld maps the screen into the camera's rect. The frame governor draws into a part of its target
    // (camera.rect) and stretches it over the whole canvas, so the point goes to where it lands in that part.
    const r = cam.rect, { width, height } = cam.system.app.graphicsDevice.clientRect;
    x = r.x * width + x * r.z;
    y = (1 - r.y - r.w) * height + y * r.w;
    cam.screenToWorld(x, y, cam.nearClip, tmpA);
    cam.screenToWorld(x, y, cam.farClip, tmpB);
    const d = tmpB.clone().sub(tmpA).normalize();
    return { origin: [tmpA.x, tmpA.y, tmpA.z], dir: [d.x, d.y, d.z] };
}

/** Critically damped spring on an angle in degrees (unwrapped, so it never spins the long way). */
export class AngleSpring {
    constructor(value = 0, omega = 18, maxRate = Infinity) {
        this.x = this.target = value;
        this.v = 0;
        this.omega = omega;
        this.maxRate = maxRate;     // deg/s cap, keeps automatic turns calm at sharp corners
    }

    /** Aim at an absolute heading by the shortest way round. */
    aim(heading) {
        this.target = this.x + wrap(heading - this.x);
    }

    set(value) {
        this.x = this.target = value;
        this.v = 0;
    }

    update(dt) {
        const w = this.omega;
        const n = Math.max(1, Math.ceil((w * dt) / 0.25));
        const h = dt / n;
        for (let i = 0; i < n; i++) {
            this.v += (w * w * (this.target - this.x) - 2 * w * this.v) * h;
            this.v = Math.max(-this.maxRate, Math.min(this.maxRate, this.v));
            this.x += this.v * h;
        }
        if (Math.abs(this.target - this.x) < 0.01 && Math.abs(this.v) < 0.05) {
            this.x = this.target;
            this.v = 0;
        }
        return this.x;
    }
}

const LOOK_OMEGA = 18;       // following the visitor's own input: snappy
const MOUSE_SENS = 0.18;     // deg per px of mouse movement in free look
const TOUCH_SENS = 0.34;     // deg per px of finger drag (the scene follows the finger)
const STICK_YAW = 120;       // deg/s at full deflection of the look stick
const STICK_PITCH = 70;
const AUTO_OMEGA = 4.2;      // automatic turns (walking to a room, arriving, tour): calm

export class WalkMode {
    /**
     * @param {import('playcanvas').Entity} camera
     * @param {import('./collision/character.js').Character} character
     * @param {object} walk - manifest.walk
     */
    constructor(camera, character, walk) {
        this.camera = camera;
        this.ch = character;
        this.walk = walk;
        this.yaw = new AngleSpring(0, LOOK_OMEGA);
        this.pitch = new AngleSpring(0, LOOK_OMEGA);
        this.vel = [0, 0];
        this.eyeY = null;
        this.path = null;            // remaining waypoints (feet positions)
        this.arrive = null;          // {yaw, pitch} to settle into at the end of the path
        this.onArrive = null;
        this.nav = null;             // NavGrid, built lazily
        this.blend = null;           // {from:[x,y,z], t} ease from a previous camera position
        this.baseFov = camera.camera.fov;   // set by main for the screen's aspect
        this.zoom = 1;               // the visitor's pinch/wheel zoom on top of it
    }

    get fov() { return Math.max(30, Math.min(112, this.baseFov * this.zoom)); }

    applyFov() {
        this.camera.camera.fov = this.fov;
    }

    get targetYaw() { return this.yaw.target; }
    set targetYaw(v) { this.yaw.aim(v); }

    setView(yaw, pitch) {
        this.yaw.set(yaw);
        this.pitch.set(pitch);
    }

    /** Ease from wherever the camera is now instead of jumping (launch, end of tour, teleport). */
    blendFrom(position, seconds = 0.7) {
        this.blend = { from: position.slice(), t: 0, seconds };
        this.eyeY = null;
    }

    /**
     * Walk along waypoints, then settle into the given view.
     * @param {number[][]} points
     * @param {{yaw?:number, pitch?:number, onArrive?:Function}} [opts]
     */
    follow(points, opts = {}) {
        this.path = points && points.length ? points.slice() : null;
        this.replans = 0;
        this.reach = 0.35;   // m: switch to the next waypoint this early (smooth cornering)
        this.arrive = opts.yaw !== undefined ? { yaw: opts.yaw, pitch: opts.pitch ?? 0 } : null;
        this.onArrive = opts.onArrive || null;
    }

    _replan() {
        if (!this.nav || this.replans >= 2) return false;
        this.replans++;
        const end = this.path[this.path.length - 1];
        const ids = this.nav.path(this.nav.nearest(...this.ch.pos, 0.5), this.nav.nearest(end[0], end[1], end[2], 0.5));
        if (!ids || ids.length < 2) return false;
        this.path = ids.map(id => this.nav._pt(id));
        this.path[this.path.length - 1] = end;
        // Tight corridor: follow the cell centres closely instead of cutting towards later points
        this.reach = this.nav.cell * 0.6;
        return true;
    }

    cancelPath() {
        this.path = null;
        this.arrive = null;
    }

    /** Point `ahead` metres further along the remaining path (for a smooth look direction). */
    _lookAhead(ahead) {
        let px = this.ch.pos[0], pz = this.ch.pos[2], left = ahead;
        for (const [x, , z] of this.path) {
            const d = Math.hypot(x - px, z - pz);
            if (d >= left) return [px + ((x - px) / d) * left, pz + ((z - pz) / d) * left];
            left -= d;
            px = x; pz = z;
        }
        return [px, pz];
    }

    _remaining() {
        let px = this.ch.pos[0], pz = this.ch.pos[2], sum = 0;
        for (const [x, , z] of this.path) { sum += Math.hypot(x - px, z - pz); px = x; pz = z; }
        return sum;
    }

    update(dt, input, touch) {
        // Look: touch drags the scene; in free look the view follows the mouse like a game camera; the look stick
        // turns it while held (squared, so small deflections aim finely)
        const [lx, ly] = input.takeLook();
        const [mx, my] = input.takeMouse();
        const [jx, jy] = input.lookStick();
        const userLook = lx || ly || mx || my || jx || jy;
        if (userLook) {
            this.arrive = null;
            this.yaw.target += lx * TOUCH_SENS - mx * MOUSE_SENS - jx * Math.abs(jx) * STICK_YAW * dt;
            this.pitch.target = clampPitch(this.pitch.target + ly * TOUCH_SENS - my * MOUSE_SENS + jy * Math.abs(jy) * STICK_PITCH * dt);
        }
        // Motion look: the view turns with the device at once (no spring lag, it is the visitor's own hand); the
        // view is theirs then, walking to a tapped point or a room does not turn it
        const [gx, gy] = input.takeGyro();
        if (gx || gy) {
            this.yaw.x += gx;
            this.yaw.target += gx;
            this.pitch.x = clampPitch(this.pitch.x + gy);
            this.pitch.target = clampPitch(this.pitch.target + gy);
        }
        const steer = !input.gyroOn;

        const intent = input.moveIntent();
        if (intent.active) this.cancelPath();
        const auto = !!this.path || !!this.arrive;
        const calm = auto && !userLook;
        this.yaw.omega = this.pitch.omega = calm ? AUTO_OMEGA : LOOK_OMEGA;
        this.yaw.maxRate = calm ? 110 : Infinity;
        this.pitch.maxRate = calm ? 40 : Infinity;

        let wx = 0, wz = 0;
        const yr = this.yaw.x * DEG;
        const fx = -Math.sin(yr), fz = -Math.cos(yr);   // camera forward on the floor
        const sx = Math.cos(yr), sz = -Math.sin(yr);
        if (intent.active) {
            const speed = intent.run ? this.walk.runSpeed : this.walk.speed;
            wx = (fx * intent.y + sx * intent.x) * speed;
            wz = (fz * intent.y + sz * intent.x) * speed;
        } else if (this.path) {
            while (this.path.length > 1 && Math.hypot(this.path[0][0] - this.ch.pos[0], this.path[0][2] - this.ch.pos[2]) < this.reach) this.path.shift();
            const [px, , pz] = this.path[0];
            const dx = px - this.ch.pos[0], dz = pz - this.ch.pos[2];
            const dist = Math.hypot(dx, dz);
            if (this.path.length === 1 && dist < 0.08) {
                this.path = null;
                this.onArrive?.();
                if (this.arrive && steer) { this.yaw.aim(this.arrive.yaw); this.pitch.target = this.arrive.pitch; }
                else this.arrive = null;
            } else {
                // Look where the path goes a little ahead, level the view while walking
                const [ax, az] = this._lookAhead(1.4);
                const heading = Math.atan2(-(ax - this.ch.pos[0]), -(az - this.ch.pos[2])) / DEG;
                if (!userLook && steer) {
                    this.yaw.aim(heading);
                    this.pitch.target = approach(this.pitch.target, -3, damp(2, dt), 0.05);
                }
                // Ease in, brake smoothly before the end, slow down while turning sharply
                const brake = Math.sqrt(2 * 1.1 * this._remaining()) + 0.05;
                const turn = Math.max(0.3, 1 - Math.abs(wrap(heading - this.yaw.x)) / 140);
                const v = Math.min(this.walk.speed, brake) * turn;
                wx = (dx / dist) * v;
                wz = (dz / dist) * v;
            }
        } else if (this.arrive && Math.abs(this.yaw.target - this.yaw.x) < 0.5 && Math.abs(this.pitch.target - this.pitch.x) < 0.5) {
            this.arrive = null;
        }
        const k = damp(this.path ? 4 : 10, dt);
        this.vel[0] = approach(this.vel[0], wx, k, 0.01);
        this.vel[1] = approach(this.vel[1], wz, k, 0.01);
        const res = this.vel[0] || this.vel[1] || !this.ch.onGround
            ? this.ch.move(this.vel[0] * dt, this.vel[1] * dt, dt)
            : { blocked: false, reset: false };
        if (res.blocked && this.path) {
            // Blocked, usually a corner cut while turning (the body lags behind the smoothed path).
            // Re-plan from here along the unsmoothed node path, which hugs the walkable cells; give
            // up only if that is blocked too, rather than push forever
            this._blockedFor = (this._blockedFor || 0) + dt;
            if (this._blockedFor > 0.6) {
                this._blockedFor = 0;
                if (!this._replan()) this.cancelPath();
            }
        } else {
            this._blockedFor = 0;
        }

        const yaw = this.yaw.update(dt);
        const pitch = this.pitch.update(dt);
        const eye = this.ch.eye;
        // Smooth height changes (stairs, sills) instead of hard jumps
        this.eyeY = this.eyeY === null || res.reset ? eye[1] : approach(this.eyeY, eye[1], damp(12, dt), 0.001);
        let pos = [eye[0], this.eyeY, eye[2]];
        if (this.blend) {
            this.blend.t += dt;
            const t = smooth(Math.min(1, this.blend.t / this.blend.seconds));
            pos = pos.map((v, i) => this.blend.from[i] + (v - this.blend.from[i]) * t);
            if (t >= 1) this.blend = null;
        }
        this.camera.setPosition(pos[0], pos[1], pos[2]);
        this.camera.setEulerAngles(pitch, yaw, 0);
        // After the drone tour (it flies with its own width) ease back to the walking view instead of jumping
        const cam = this.camera.camera;
        if (cam && cam.fov !== this.fov) cam.fov = approach(cam.fov, this.fov, damp(4, dt), 0.01);

        const zoom = input.takeZoom();
        if (zoom) {
            this.zoom = Math.max(0.45, Math.min(1.12, this.zoom * Math.exp(zoom * 0.0005)));
            this.applyFov();
        }
    }
}

export class OverviewMode {
    constructor(camera, bounds) {
        this.camera = camera;
        const c = [(bounds.min[0] + bounds.max[0]) / 2, 0, (bounds.min[2] + bounds.max[2]) / 2];
        this.target = c;
        const size = Math.max(bounds.max[0] - bounds.min[0], bounds.max[2] - bounds.min[2]);
        this.dist = this.targetDist = size * 1.1;
        this.minDist = size * 0.3;
        this.maxDist = size * 2.5;
        this.yaw = new AngleSpring(30, 10);
        this.pitch = new AngleSpring(-55, 10);
    }

    update(dt, input) {
        const [lx, ly] = input.takeLook();
        const [mx, my] = input.takeMouse();
        input.takeGyro();   // motion look is for walking; the dollhouse turns by touch
        this.yaw.target += -(lx + mx) * 0.42;
        this.pitch.target = Math.max(-89, Math.min(-15, this.pitch.target - (ly + my) * 0.42));
        const z = input.takeZoom();
        if (z) this.targetDist = Math.max(this.minDist, Math.min(this.maxDist, this.targetDist * Math.exp(z * 0.001)));
        this.dist = approach(this.dist, this.targetDist, damp(10, dt), 0.001);
        const yaw = this.yaw.update(dt), pitch = this.pitch.update(dt);
        const yr = yaw * DEG, pr = pitch * DEG;
        const x = this.target[0] + Math.sin(yr) * Math.cos(pr) * this.dist;
        const y = this.target[1] - Math.sin(pr) * this.dist;
        const zz = this.target[2] + Math.cos(yr) * Math.cos(pr) * this.dist;
        this.camera.setPosition(x, y, zz);
        this.camera.setEulerAngles(pitch, yaw, 0);
    }
}

/**
 * Automatic drone flight through the rooms: start, every room in manifest order, flown like a one-take
 * FPV real-estate film (measured on luxury tours, docs/drohnentour/): chest height, level camera, about
 * walking pace, never standing still, turning only while moving.
 * The route follows the navigation grid (so it never cuts through walls), is smoothed into a
 * Catmull-Rom curve at drone height (lowered under low ceilings and door lintels) and checked against
 * the voxels. The drone accelerates and brakes gently, looks ahead along the curve and slows down while
 * the view still has to turn, so turns happen during slow travel instead of on the spot.
 *
 * Points of interest (set per object by the agent, `pois`) bend the flight: approaching one, the drone
 * slows down and the camera swings towards it and keeps it framed while passing (pan and tilt), then
 * lets go; a point with `dwell` makes the drone crawl past it. Rooms are not stops: the drone slows to a
 * crawl at each room's viewpoint, turns into the room's view on the way in and keeps it while it picks
 * up speed again. Only the last room ends in a short hover.
 */
export class DroneTour {
    /**
     * @param {import('playcanvas').Entity} camera
     * @param {import('./collision/character.js').Character} character - for headroom queries
     * @param {import('./collision/navgrid.js').NavGrid} nav
     * @param {{position:number[], yaw:number, name?:string, id?:string}[]} stops - eye positions
     * @param {object} [opts]
     * @param {{position:number[], radius?:number, weight?:number, dwell?:number, id?:string}[]} [opts.pois]
     * @param {number} [opts.baseFov] - the walking view's vertical FOV (default: the camera's at start)
     */
    constructor(camera, character, nav, stops, opts = {}) {
        this.camera = camera;
        this.ch = character;
        this.nav = nav;
        // Defaults measured on 10 continuous luxury FPV tours (docs/drohnentour/referenzen.md)
        this.height = opts.height ?? 1.2;      // eye height above floor while flying (chest height)
        this.speed = opts.speed ?? 1.0;        // m/s cruise
        this.accel = opts.accel ?? 0.5;        // m/s² for starting and braking
        this.crawl = opts.crawl ?? 0.25;       // m/s at each room's viewpoint: slow down, never stop
        this.minSpeed = opts.minSpeed ?? 0.2;  // m/s floor while flying (near points of interest)
        this.endHold = opts.endHold ?? 3.0;    // s drifting back out of the last room before handing back
        this.tilt = opts.tilt ?? 0;            // deg pitch while flying: level, verticals stay vertical
        this.maxHFov = opts.maxHFov ?? 100;    // deg horizontal: wide like FPV, edges not yet stretched
        this.pois = (opts.pois || []).filter(p => Array.isArray(p.position));
        this.onStop = opts.onStop || null;     // (stop) => void when passing a room's viewpoint
        this.baseFov = opts.baseFov ?? camera.camera?.fov ?? null;
        this.legs = [];
        this.stops = stops;
        this.yaw = new AngleSpring(0, 4.5, opts.maxTurn ?? 45);
        this.pitch = new AngleSpring(0, 4, 7);
        this.leg = 0;
        this.s = 0;                            // arc length flown on the current leg
        this.v = 0;                            // current speed
        this.t = 0;                            // timer for the point-of-interest and end phases
        this.phase = 'fly';                    // 'fly' | 'poi' | 'end'
        this.poiHold = null;
        this.held = new Set();                 // points already crawled past (once per tour)
        this.done = false;
    }

    /** Build all legs from the camera's current pose. Returns false if no route exists. */
    start() {
        const p = this.camera.getPosition(), f = this.camera.forward;
        // From the view direction: getEulerAngles() returns the mirrored form (pitch ~180°) for headings
        // beyond ±90°, which made the drone start by swinging over the ceiling
        if (f) {
            this.yaw.set(Math.atan2(-f.x, -f.z) / DEG);
            this.pitch.set(Math.asin(Math.max(-1, Math.min(1, f.y))) / DEG);
        } else {
            const e = this.camera.getEulerAngles();
            this.yaw.set(e.y);
            this.pitch.set(e.x);
        }
        // Route ends are looked up on the floor (feet), never at flying height: a query 1.2 m below an
        // eye point lands on bed and sofa tops, which are islands of their own in the walkable grid
        const eye = this.ch.agent?.eyeHeight ?? 1.6;
        let prev = [p.x, p.y, p.z], prevFeet = this.ch.pos[1];
        for (const stop of this.stops) {
            const curve = this._route(prev, prevFeet, stop.position, stop.position[1] - eye);
            if (!curve) continue;
            // Points with a dwell time: pause at the closest approach, once
            const holds = [];
            for (const poi of this.pois) {
                if (!(poi.dwell > 0)) continue;
                let best = Infinity, bs = 0;
                for (let s = 0; s <= curve.length; s += 0.1) {
                    const d = dist(curve.at(s), poi.position);
                    if (d < best) { best = d; bs = s; }
                }
                if (best <= (poi.radius ?? 2.5)) holds.push({ poi, s: bs, done: false });
            }
            this.legs.push({ curve, stop, holds: holds.sort((x, y) => x.s - y.s) });
            // Next leg starts exactly where this one ends (drone height), not at the stored stop
            prev = curve.at(curve.length);
            prevFeet = curve.endFeet;
        }
        // Each room's view; a stop without one (hand-edited manifest) keeps the direction of arrival
        for (const leg of this.legs) {
            const d = flatDir(leg.curve, leg.curve.length - 0.4, leg.curve.length);
            leg.yaw = Number.isFinite(leg.stop.yaw) ? leg.stop.yaw : Math.atan2(-d[0], -d[1]) / DEG;
        }
        // Speed at each room's viewpoint: a crawl, slower still where the route doubles back (dead-end
        // rooms) so the reversal is barely visible; the last leg ends at rest
        for (let i = 0; i < this.legs.length; i++) {
            const a = this.legs[i], b = this.legs[i + 1];
            if (!b) { a.vEnd = 0; break; }
            const d0 = flatDir(a.curve, a.curve.length - 0.4, a.curve.length), d1 = flatDir(b.curve, 0, 0.4);
            a.vEnd = Math.max(0.05, this.crawl * (1 + d0[0] * d1[0] + d0[1] * d1[1]) / 2);
            b.prevYaw = a.yaw;
        }
        this.leg = 0;
        this.s = 0;
        this.v = 0;
        this.phase = 'fly';
        return this.legs.length > 0;
    }

    /** Strongest point of interest seen from `pos` while heading `heading`: {poi, weight} or null. */
    _focus(pos, heading) {
        let best = null, bw = 0;
        for (const poi of this.pois) {
            const r = poi.radius ?? 2.5;
            const d = dist(pos, poi.position);
            if (d >= r) continue;
            let w = (poi.weight ?? 1) * smooth(1 - d / r);
            if (heading !== null) {
                // Things far behind the drone do not pull the camera round
                const toPoi = Math.atan2(-(poi.position[0] - pos[0]), -(poi.position[2] - pos[2])) / DEG;
                w *= smooth(Math.max(0, Math.min(1, (160 - Math.abs(wrap(toPoi - heading))) / 60)));
            }
            if (w > bw) { bw = w; best = poi; }
        }
        return best ? { poi: best, weight: Math.min(1, bw) } : null;
    }

    _aimAt(pos, target, blend, baseYaw, basePitch) {
        const dx = target[0] - pos[0], dz = target[2] - pos[2];
        const yaw = Math.atan2(-dx, -dz) / DEG;
        const pitch = Math.max(-40, Math.min(20, Math.atan2(target[1] - pos[1], Math.hypot(dx, dz)) / DEG));
        this.yaw.aim(baseYaw + wrap(yaw - baseYaw) * blend);
        this.pitch.target = basePitch + (pitch - basePitch) * blend;
    }

    _route(fromEye, fromFeet, toEye, toFeet) {
        const nav = this.nav;
        const a = nav.nearest(fromEye[0], fromFeet, fromEye[2], 1.5);
        const b = nav.nearest(toEye[0], toFeet, toEye[2], 1.5);
        const ids = nav.path(a, b);
        if (!ids) return null;
        const feet = nav.smooth(ids);
        // Control points at drone height (kept below ceilings and door lintels), every ~0.3 m along
        // the straight segments, so the curve only rounds the corners and cannot swing into walls
        const pts = [fromEye.slice()], caps = [Infinity], floors = [fromFeet];
        let prevFeet = [fromEye[0], feet[0][1], fromEye[2]];
        for (const f of feet.slice(1)) {
            const n = Math.max(1, Math.round(Math.hypot(f[0] - prevFeet[0], f[2] - prevFeet[2]) / 0.3));
            for (let k = 1; k <= n; k++) {
                const t = k / n;
                const q = prevFeet.map((v, i) => v + (f[i] - v) * t);
                const cap = this._headroom(q);
                pts.push([q[0], q[1] + Math.max(Math.min(1.0, this.height), Math.min(this.height, cap)), q[2]]);
                caps.push(q[1] + cap);
                floors.push(q[1]);
            }
            prevFeet = f;
        }
        // Ease from the starting height (the visitor's eye is higher than the drone flies) instead of
        // diving: over at least 1.5 m, at most a third of a metre of height per metre travelled
        const dy0 = pts.length > 1 ? fromEye[1] - pts[1][1] : 0;
        if (Math.abs(dy0) > 0.02) {
            const span = Math.max(1.5, 3 * Math.abs(dy0));
            for (let i = 1, d = 0; i < pts.length; i++) {
                d += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][2] - pts[i - 1][2]);
                if (d >= span) break;
                pts[i][1] = Math.min(caps[i], pts[i][1] + dy0 * (1 - smooth(d / span)));
            }
        }
        // Even out clearance dips (door lintels) so the drone ducks smoothly instead of stepping
        for (let pass = 0; pass < 3; pass++) {
            for (let i = 1; i < pts.length - 1; i++) pts[i][1] = Math.min(pts[i][1], (pts[i - 1][1] + pts[i][1] + pts[i + 1][1]) / 3 + 0.02);
        }
        // Rare tight spots: fly the straight segments between walkable points instead
        let curve = sampleCurve(pts);
        if (!this._clear(curve)) curve = sampleCurve(pts, true);
        if (!this._clear(curve)) return null;
        curve.endFeet = nav.nodes[b].y;
        // Floor under each control point, for landing mid-flight (also on stairs)
        curve.floorAt = (x, y, z) => {
            let best = 0, bd = Infinity;
            for (let i = 0; i < pts.length; i++) {
                const d = Math.hypot(pts[i][0] - x, pts[i][1] - y, pts[i][2] - z);
                if (d < bd) { bd = d; best = i; }
            }
            return floors[best];
        };
        return curve;
    }

    /** True if the camera (with a small margin) stays in free space along the whole curve. */
    _clear(curve) {
        // 0.15 m keeps every room of the test flats reachable; a wider margin needs door centring first
        const g = this.ch.grid, m = 0.15;
        for (let s = 0; s <= curve.length; s += 0.05) {
            const [x, y, z] = curve.at(s);
            if (g.solidAt(x, y, z) || g.solidAt(x + m, y, z) || g.solidAt(x - m, y, z) ||
                g.solidAt(x, y, z + m) || g.solidAt(x, y, z - m) || g.solidAt(x, y + 0.06, z)) return false;
        }
        return true;
    }

    /** Highest eye height above these feet that keeps 0.2 m below anything overhead (lintel, ceiling). */
    _headroom(feet) {
        const g = this.ch.grid, r = 0.12;
        let lowest = Infinity;
        // Check a small cross around the point: the camera passes near door jambs and lintel edges
        for (const [ox, oz] of [[0, 0], [r, 0], [-r, 0], [0, r], [0, -r]]) {
            const ix = g.ix(feet[0] + ox), iz = g.iz(feet[2] + oz);
            for (let iy = g.iy(feet[1] + 1.0); g.top(iy) - feet[1] < Math.max(this.height, 1.8) + 0.6; iy++) {
                if (g.solid(ix, iy, iz)) { lowest = Math.min(lowest, g.min[1] + iy * g.res - feet[1]); break; }
            }
        }
        return lowest - 0.2;
    }

    /** The drone's view (degrees), for handing over to walking without the Euler mirroring. */
    get view() { return { yaw: this.yaw.x, pitch: this.pitch.x }; }

    /** Feet on the floor below the drone, for handing the camera back to walking. */
    landing() {
        const p = this.camera.getPosition();
        // The floor under the nearest route point (right on stairs too), not the drone's height minus a
        // guess: that could find a bed or sofa top
        const c = this.legs[this.leg]?.curve;
        const floor = c ? c.floorAt(p.x, p.y, p.z) : p.y - this.height;
        const id = this.nav.nearest(p.x, floor, p.z, 1.0);
        return [p.x, id >= 0 ? this.nav.nodes[id].y : floor, p.z];
    }

    /** Advance; returns false once the tour has finished. */
    update(dt) {
        if (this.done) return false;
        const leg = this.legs[this.leg];
        if (!leg) { this.done = true; return false; }
        const c = leg.curve;
        const pos = c.at(this.s);
        const ahead = c.at(Math.min(c.length, this.s + 2.2));
        const hx = ahead[0] - pos[0], hz = ahead[2] - pos[2];
        const flyHeading = Math.hypot(hx, hz) > 0.05 ? Math.atan2(-hx, -hz) / DEG : this.yaw.target;
        const remaining = c.length - this.s;

        if (this.phase === 'end') {
            // Last room: drift slowly back the way the drone came in, the view resting on the room and
            // panning a little, then hand back (pilots end on a pull-out, not a dead stop)
            this.t += dt;
            const back = flatDir(c, c.length, c.length - 0.5);
            const step = 0.25 * Math.sin(Math.PI * Math.min(1, this.t / this.endHold)) * dt;
            const g = this.ch.grid, end = c.at(c.length), o = (this.backOff || 0) + step;
            const q = [end[0] + back[0] * o, end[1], end[2] + back[1] * o];
            if (![[0, 0], [0.25, 0], [-0.25, 0], [0, 0.25], [0, -0.25]].some(([dx, dz]) => g.solidAt(q[0] + dx, q[1], q[2] + dz))) this.backOff = o;
            this.yaw.aim(leg.yaw + 8 * Math.sin((this.t / this.endHold) * Math.PI));
            this.pitch.target = this.tilt;
            if (this.t >= this.endHold) this.done = true;
        } else {
            // Speed: cruise, slower near points of interest (looking a metre ahead, so braking starts
            // early) and while the view still has to turn; gentle acceleration; braking to a crawl at each
            // room's viewpoint, to rest only at the last one
            const f0 = this._focus(pos, flyHeading), f1 = this._focus(c.at(Math.min(c.length, this.s + 1)), flyHeading);
            const interest = Math.max(f0?.weight ?? 0, f1?.weight ?? 0);
            const turning = Math.max(0.35, 1 - Math.abs(wrap(this.yaw.target - this.yaw.x)) / 120);
            const cruise = this.phase === 'poi' ? this.minSpeed : Math.max(this.minSpeed, this.speed * (1 - 0.7 * interest) * turning);
            const target = Math.min(cruise, Math.sqrt(leg.vEnd ** 2 + 2 * this.accel * remaining) + 0.01);
            this.v += Math.max(-this.accel * 2 * dt, Math.min(this.accel * dt, target - this.v));
            const prevS = this.s;
            this.s = Math.min(c.length, this.s + this.v * dt);

            // Look: ahead along the curve, swung towards a point of interest; around a room's viewpoint the
            // room's view, turned to on the way in and held while the drone picks up speed again
            const into = smooth(Math.max(0, Math.min(1, 1 - remaining / 2.0)));
            const out = leg.prevYaw === undefined ? 0 : smooth(Math.max(0, Math.min(1, 1 - this.s / 1.8)));
            let baseYaw = flyHeading;
            if (out) baseYaw += wrap(leg.prevYaw - baseYaw) * out;
            baseYaw += wrap(leg.yaw - baseYaw) * into;
            if (this.phase === 'poi') this._aimAt(pos, this.poiHold.position, 1, baseYaw, this.tilt);
            // Full framing well before the closest approach; the weight fades in and out smoothly
            else if (f0) this._aimAt(pos, f0.poi.position, smooth(Math.min(1, f0.weight * 2.2)) * (1 - Math.max(into, out)), baseYaw, this.tilt);
            else { this.yaw.aim(baseYaw); this.pitch.target = this.tilt; }

            if (this.phase === 'poi') {
                // Crawl past a point with a dwell time, framing it
                this.t += dt;
                if (this.t >= this.poiHold.dwell) { this.phase = 'fly'; this.poiHold = null; }
            } else {
                const hold = leg.holds.find(h => !h.done && !this.held.has(h.poi) && prevS <= h.s && this.s >= h.s);
                if (hold) {
                    hold.done = true;
                    this.held.add(hold.poi);
                    this.phase = 'poi';
                    this.poiHold = hold.poi;
                    this.t = 0;
                }
            }
            if (remaining <= 0.005 || this.s >= c.length) {
                this.onStop?.(leg.stop);
                this.poiHold = null;
                if (this.leg === this.legs.length - 1) {
                    this.phase = 'end';
                    this.t = 0;
                    this.v = 0;
                } else {
                    // Carry the crawl speed into the next leg (it starts where this one ended)
                    this.phase = 'fly';
                    this.leg++;
                    this.s = 0;
                }
            }
        }
        const p = (this.legs[this.leg] ?? leg).curve.at(this.s);
        if (this.phase === 'end' && this.backOff) {
            const back = flatDir(c, c.length, c.length - 0.5);
            p[0] += back[0] * this.backOff;
            p[2] += back[1] * this.backOff;
        }
        this.camera.setPosition(p[0], p[1], p[2]);
        this.camera.setEulerAngles(this.pitch.update(dt), this.yaw.update(dt), 0);
        // Wide like an FPV lens, but capped where splat edges would stretch; never wider than the walking view
        const cam = this.camera.camera;
        if (cam && this.baseFov) {
            const dev = cam.system?.app?.graphicsDevice;
            const aspect = dev ? dev.width / Math.max(1, dev.height) : 16 / 9;
            const want = Math.min(this.baseFov, 2 * Math.atan(Math.tan(this.maxHFov * DEG / 2) / aspect) / DEG);
            cam.fov = approach(cam.fov, want, damp(3, dt), 0.01);
        }
        return !this.done;
    }
}

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

/** Unit direction on the floor from curve point s0 to s1. */
function flatDir(curve, s0, s1) {
    const a = curve.at(Math.max(0, s0)), b = curve.at(Math.min(curve.length, s1));
    const dx = b[0] - a[0], dz = b[2] - a[2], d = Math.hypot(dx, dz) || 1;
    return [dx / d, dz / d];
}

/** Centripetal Catmull-Rom (or straight lines) through the points, resampled by arc length. */
function sampleCurve(pts, linear = false) {
    if (pts.length === 1) pts = [pts[0], pts[0]];
    const ext = [pts[0], ...pts, pts[pts.length - 1]];
    const out = [];
    for (let i = 1; i < ext.length - 2; i++) {
        const [p0, p1, p2, p3] = [ext[i - 1], ext[i], ext[i + 1], ext[i + 2]];
        const segLen = Math.hypot(p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]);
        const n = Math.max(2, Math.ceil(segLen / 0.05));
        for (let s = 0; s < n; s++) out.push(linear ? p1.map((v, k) => v + (p2[k] - v) * (s / n)) : catmull(p0, p1, p2, p3, s / n));
    }
    out.push(pts[pts.length - 1].slice());
    const acc = [0];
    for (let i = 1; i < out.length; i++) acc.push(acc[i - 1] + Math.hypot(out[i][0] - out[i - 1][0], out[i][1] - out[i - 1][1], out[i][2] - out[i - 1][2]));
    const length = acc[acc.length - 1];
    return {
        length,
        at(s) {
            s = Math.max(0, Math.min(length, s));
            let lo = 0, hi = acc.length - 1;
            while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (acc[mid] < s) lo = mid; else hi = mid; }
            const t = acc[hi] > acc[lo] ? (s - acc[lo]) / (acc[hi] - acc[lo]) : 0;
            return out[lo].map((v, k) => v + (out[hi][k] - v) * t);
        }
    };
}

function catmull(p0, p1, p2, p3, t) {
    // Centripetal parameterisation avoids loops and overshoot at sharp corners (door frames)
    const d = (a, b) => Math.pow(Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]), 0.5) || 1e-4;
    const t0 = 0, t1 = t0 + d(p0, p1), t2 = t1 + d(p1, p2), t3 = t2 + d(p2, p3);
    const u = t1 + (t2 - t1) * t;
    const lerp = (a, b, ta, tb) => a.map((v, k) => (ta === tb ? v : ((tb - u) / (tb - ta)) * v + ((u - ta) / (tb - ta)) * b[k]));
    const A1 = lerp(p0, p1, t0, t1), A2 = lerp(p1, p2, t1, t2), A3 = lerp(p2, p3, t2, t3);
    const B1 = lerp(A1, A2, t0, t2), B2 = lerp(A2, A3, t1, t3);
    return lerp(B1, B2, t1, t2);
}
